import { useEffect, useRef, useState } from 'react';

const MAX_TITLE_WIDTH = 700;
const DEFAULT_TITLE_WIDTH = 400;
const MIN_TITLE_WIDTH = 250;
const TABS_GAP = 16;

export function useResourceLayoutMeasurement() {
  const containerRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const [titleMaxWidth, setTitleMaxWidth] = useState<string>(
    `${DEFAULT_TITLE_WIDTH}px`
  );

  useEffect(() => {
    const updateMaxWidth = () => {
      const containerWidth = containerRef.current?.offsetWidth || 0;
      const tabsWidth = tabsRef.current?.offsetWidth || 0;
      const actionsWidth = actionsRef.current?.offsetWidth || 0;

      if (!containerRef.current) return;

      // Get computed styles to calculate actual gap and padding values
      const containerStyles = getComputedStyle(containerRef.current);
      const paddingLeft = parseFloat(containerStyles.paddingLeft) || 0;
      const paddingRight = parseFloat(containerStyles.paddingRight) || 0;
      const totalPadding = paddingLeft + paddingRight;

      // Find the inner flex container (the one with gap-x-8)
      const innerFlexContainer = containerRef.current?.querySelector(
        '.flex.flex-wrap.items-start'
      ) as HTMLElement;
      let gapValue = 0;

      if (innerFlexContainer) {
        const innerStyles = getComputedStyle(innerFlexContainer);
        // columnGap corresponds to gap-x in Tailwind
        gapValue = parseFloat(innerStyles.columnGap) || 0;
      }

      // Additional spacing between the flex container and actions (flex-grow space)
      const additionalGap = TABS_GAP;

      const totalGap = gapValue + additionalGap;

      const availableWidth =
        containerWidth - tabsWidth - actionsWidth - totalGap - totalPadding;
      const maxWidth = Math.min(MAX_TITLE_WIDTH, availableWidth);

      if (maxWidth < MIN_TITLE_WIDTH) {
        // if we really can't fit title in 1 line and min 250px, we'll
        // just wrap and use max 700px width.
        setTitleMaxWidth('700px');
        return;
      }

      setTitleMaxWidth(`${maxWidth}px`);
    };

    // Initial measurement
    updateMaxWidth();

    // Set up ResizeObserver to watch for changes
    const resizeObserver = new ResizeObserver(() => {
      updateMaxWidth();
    });

    if (containerRef.current) resizeObserver.observe(containerRef.current);
    if (tabsRef.current) resizeObserver.observe(tabsRef.current);
    if (actionsRef.current) resizeObserver.observe(actionsRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return {
    containerRef,
    tabsRef,
    actionsRef,
    titleMaxWidth,
  };
}
